import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import {
  fetchAlertDetails,
  dispatchAction,
  fetchSensorAlertDetails,
  closeAlert
} from "../../services/alertService";
import { toast } from "react-toastify";
import { useAlertWebSocket } from "../../contexts/WebSocketContext";
import { ENDPOINTS, API_BASE_URL } from "../../utils/appConfig";
import { getItem } from "../../utils/authStorage";
import { transformLLMResponse } from "../../utils/llmTransformer";
import {
  formatLocalDateTime,
  formatLocalTime
} from "../../utils/helpers";
import {
  TablerArrowLeft,
  TablerDrone,
  TablerX,
  TablerHigh,
  TablerMedium,
  TablerLow,
  ShieldIcon,
  TablerAlertTriangle
} from "../shared/Icons";
import WhepVideoPlayer from "../shared/WhepVideoPlayer";
import { ReactComponent as GroupIcon } from "../../assets/group.svg";
import "./AlertDetailPage.css";

function AlertDetailPage({ alert: propAlert, onClose }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const alertIdFromUrl = searchParams.get("alertId");
  const alertTypeFromUrl = searchParams.get("type");
  const { alertId: paramAlertId } = useParams();

  const activeAlertId = alertIdFromUrl || paramAlertId || (propAlert ? propAlert.id : null);
  const activeAlertType = alertTypeFromUrl || (propAlert ? propAlert.type : "camera");

  const [localAlert, setLocalAlert] = useState(null);
  const [deviceStates, setDeviceStates] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [videoRetryCount, setVideoRetryCount] = useState(0);

  // Playable video implementation states
  const [isPlaying, setIsPlaying] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [elapsedTime, setElapsedTime] = useState("0:00");
  const [totalDuration, setTotalDuration] = useState("0:00");
  const playerRef = React.useRef(null);
  const videoContainerRef = React.useRef(null);
  const videoPollTimerRef = React.useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleMaximize = () => {
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const [showCloseInput, setShowCloseInput] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [isSubmittingClose, setIsSubmittingClose] = useState(false);
  const [showCannotCloseModal, setShowCannotCloseModal] = useState(false);

  const [disabledRTL, setDisabledRTL] = useState(false);
  const [disabledLAND, setDisabledLAND] = useState(false);
  const [ptzAngle, setPtzAngle] = useState(0);

  const [showMissionDialog, setShowMissionDialog] = useState(false);
  const [missionData, setMissionData] = useState(null);
  const [dispatchEdgeUrl, setDispatchEdgeUrl] = useState(null);
  const [droneStreamEnded, setDroneStreamEnded] = useState(false);

  // Pagination for Observed Activity
  const [activityPage, setActivityPage] = useState(1);
  const activitiesPerPage = 5;

  const username = getItem("username") || "User";
  const rawRole = getItem("role") || "Operator";
  const role = rawRole.toLowerCase();

  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || '').toLowerCase();
    return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const formatBadgeLabel = (value) => {
    if (!value) return "N/A";
    return String(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const { subscribe, sendMessage } = useAlertWebSocket();

  const currentAlert = localAlert || propAlert;

  const isPrimaryDevice = useCallback((device) => {
    return (
      device?.primary === true ||
      device?.primary === "true" ||
      device?.primary_device === true ||
      device?.primary_device === "true"
    );
  }, []);

  const displayedDevices = useMemo(() => {
    if (!currentAlert?.devices || !Array.isArray(currentAlert.devices)) return [];
    return [...currentAlert.devices].sort((a, b) => {
      const aPrimary = isPrimaryDevice(a);
      const bPrimary = isPrimaryDevice(b);
      if (aPrimary === bPrimary) return 0;
      return aPrimary ? -1 : 1;
    });
  }, [currentAlert, isPrimaryDevice]);

  // Derive the selected device index from its stable ID so the selection
  // survives array reordering (e.g. a dispatched drone being inserted before
  // the camera). Without this, selectedDeviceIndex stays at 0 and points to
  // whichever device landed there after refetch, swapping the camera feed
  // for the drone stream.
  const selectedDeviceIndex = useMemo(() => {
    if (!selectedDeviceId) return 0;
    const idx = displayedDevices.findIndex((d) => d.id === selectedDeviceId);
    return idx >= 0 ? idx : 0;
  }, [selectedDeviceId, displayedDevices]);

  // Load alert details
  const loadAlert = useCallback(async () => {
    if (!activeAlertId) return;
    try {
      const fetchFn = activeAlertType === "sensor" ? fetchSensorAlertDetails : fetchAlertDetails;
      const data = await fetchFn(activeAlertId);
      setLocalAlert(data);
    } catch (e) {
      console.error("Failed to fetch alert details", e);
    }
  }, [activeAlertId, activeAlertType]);

  useEffect(() => {
    if (propAlert && (propAlert.devices || propAlert.message || propAlert.start_time)) {
      setLocalAlert(propAlert);
    } else {
      loadAlert();
    }
  }, [propAlert, loadAlert]);

  // Subscribe to real-time updates — targeted inline merge
  useEffect(() => {
    let debounceTimer;
    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadAlert();
      }, 500);
    };

    const unsub = subscribe((raw) => {
      const type = raw?.type;
      const data = raw?.message || raw?.payload || raw?.data;

      if (type === "drone.mission") {
        setMissionData(data);
        setShowMissionDialog(true);
        return;
      }

      const id = data?.alert_id || data?.id;
      if (id !== activeAlertId) return;

      if (type === "alert.update" || type === "alert.verify") {
        setLocalAlert(prev => {
          if (!prev) return prev;

          // Staleness guard — skip if this event is older than current state
          if (data?.updated_at && prev?._ws_updated_at) {
            if (new Date(data.updated_at) <= new Date(prev._ws_updated_at)) return prev;
          }

          const patch = {
            _ws_updated_at: data?.updated_at ?? prev?._ws_updated_at,
          };

          if (type === "alert.verify") {
            patch.alert_analysis = "Human Verification";
          }

          // Merge llm_response inline if the payload carries it (from lvlm_inspector emit)
          if (data?.llm_response) {
            patch.llm_response = transformLLMResponse(data.llm_response);
          }

          // Merge devices and video_url if they are present in payload
          if (data?.devices) {
            patch.devices = data.devices;
          }
          if (data?.video_url) {
            patch.video_url = data.video_url;
          }

          return { ...prev, ...patch };
        });
        scheduleRefetch();
        return;
      }

      if (type === "alert.completed" || type === "alert.complete" || type === "done.alert") {
        setLocalAlert(prev => {
          if (!prev) return prev;

          const patch = {
            is_active: false,
            alert_analysis: data?.status ?? "Complete",
          };

          if (data?.completed_at) {
            patch.end_time = data.completed_at;
          }

          // Merge llm_response inline if the payload carries it
          if (data?.llm_response) {
            patch.llm_response = transformLLMResponse(data.llm_response);
          }

          if (data?.updated_at) {
            patch._ws_updated_at = data.updated_at;
          }

          // Merge devices and video_url if they are present in payload
          if (data?.devices) {
            patch.devices = data.devices;
          }
          if (data?.video_url) {
            patch.video_url = data.video_url;
          }

          return { ...prev, ...patch };
        });
        scheduleRefetch();
        return;
      }

      if (type === "drone.streaming" || type === "device.streaming" || type === "drone_flight.video_annotated") {
        scheduleRefetch();
      }
    });
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsub();
    };
  }, [subscribe, activeAlertId, loadAlert]);


  useEffect(() => {
    if (displayedDevices.length) {
      setDeviceStates(displayedDevices.map(() => ({ isLoading: false, hasError: false })));
    } else {
      setDeviceStates([]);
    }
  }, [displayedDevices]);

  const deviceIndexInitialized = useRef(false);

  useEffect(() => {
    if (!displayedDevices.length) return;
    if (!deviceIndexInitialized.current) {
      const primaryIndex = displayedDevices.findIndex(isPrimaryDevice);
      const idx = primaryIndex >= 0 ? primaryIndex : 0;
      setSelectedDeviceId(displayedDevices[idx]?.id ?? null);
      deviceIndexInitialized.current = true;
    }
  }, [displayedDevices, isPrimaryDevice]);

  // After a drone stream ends, the backend may report a transient
  // "No recording data" failure while the video is still uploading.
  // Poll for up to videoRetryCount minutes so the recorded video can arrive
  // before we surface a permanent failure banner.
  const hasDroneNoRecordingFailure = displayedDevices.some(
    (d) =>
      d.device_type?.toLowerCase() === "drone" &&
      d.failure_reason?.toLowerCase().includes("no recording data")
  );

  // True while we are still polling for a drone's recording that hasn't
  // finished uploading yet. During this window we suppress the red failure
  // banner and show a "processing" indicator instead.
  const isDroneNoRecordingWaiting = videoRetryCount > 0 && hasDroneNoRecordingFailure;

  // Tracks the previous failure state so we only stop polling when the failure
  // transitions from present to cleared (video arrived). This avoids the early
  // reset that happens on the first render before loadAlert() returns data.
  const prevDroneFailureRef = useRef(false);

  // Stop polling early once the drone's "No recording data" failure clears,
  // meaning the recording has finished uploading.
  useEffect(() => {
    const wasFailing = prevDroneFailureRef.current;
    const isFailing = hasDroneNoRecordingFailure;
    prevDroneFailureRef.current = isFailing;
    if (videoRetryCount <= 0) return;
    if (wasFailing && !isFailing) {
      setVideoRetryCount(0);
    }
  }, [hasDroneNoRecordingFailure, videoRetryCount]);

  // Schedule the next poll every minute while retries remain.
  useEffect(() => {
    if (videoRetryCount <= 0) return;
    videoPollTimerRef.current = setTimeout(() => {
      setVideoRetryCount((prev) => Math.max(0, prev - 1));
      loadAlert();
    }, 60000);
    return () => {
      if (videoPollTimerRef.current) clearTimeout(videoPollTimerRef.current);
    };
  }, [videoRetryCount, loadAlert]);

  useEffect(() => {
    setIsPlaying(false);
    setProgressPercent(0);
    setElapsedTime("0:00");
    setTotalDuration("0:00");
    if (playerRef.current) {
      try {
        playerRef.current.load();
      } catch (err) {
        console.error("Failed to load new video player source:", err);
      }
    }
  }, [selectedDeviceIndex]);

  const handleSignOut = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleCloseToggle = () => {
    const currentStatus = (currentAlert?.alert_analysis || "").toLowerCase();
    if (currentStatus === "active" || currentStatus === "inferencing") {
      setShowCannotCloseModal(true);
      return;
    }
    setShowCloseInput((prev) => {
      // When collapsing (prev is true → going false), also clear the reason
      if (prev) setCloseReason("");
      return !prev;
    });
  };

  const handleCloseCancel = () => {
    setShowCloseInput(false);
    setCloseReason("");
  };

  const handleCloseSubmit = async () => {
    const reason = (closeReason || "").trim();
    if (!reason) {
      toast.error("Please enter a reason to close the alert");
      return;
    }
    if (!currentAlert?.id) {
      toast.error("No alert selected");
      return;
    }
    setIsSubmittingClose(true);
    try {
      const isSensor = currentAlert.type === "sensor";
      await closeAlert(currentAlert.id, isSensor, reason);
      setLocalAlert((prev) =>
        prev
          ? {
              ...prev,
              alert_analysis: "complete",
              is_resolved: true,
              resolved_at: new Date().toISOString(),
              resolution_notes: reason,
              resolved_by: username,
            }
          : prev
      );
      toast.success("Alert closed successfully");
    } catch (err) {
      console.error("Failed to close alert", err);
      toast.error("Failed to close alert");
    } finally {
      setIsSubmittingClose(false);
      setShowCloseInput(false);
      setCloseReason("");
    }
  };

  const sendDroneCommand = async (cmd) => {
    if (!currentAlert?.drone) {
      toast.error("Drone information not available");
      return;
    }
    const droneId = currentAlert.drone?.id;
    if (!droneId) {
      toast.error("Drone ID not available");
      return;
    }

    try {
      const url =
        cmd === "RTL"
          ? ENDPOINTS.DRONE_RETURN_TO_LAUNCH(droneId)
          : ENDPOINTS.DRONE_EMERGENCY_LAND(droneId);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        if (cmd === "RTL") {
          setDisabledRTL(true);
          setDisabledLAND(true);
        } else {
          setDisabledLAND(true);
          setDisabledRTL(true);
        }
        toast.success(`${cmd} command sent successfully`);
      } else {
        toast.error(`${cmd} failed: ${res.status}`);
      }
    } catch (err) {
      console.error(`${cmd} request error`, err);
      toast.error(`${cmd} request failed`);
    }
  };

  const handleDispatch = async (action) => {
    try {
      const primaryDevice = currentAlert.devices?.find((d) => d.primary_device);
      const latitude = primaryDevice?.camera?.latitude;
      const longitude = primaryDevice?.camera?.longitude;

      const payload = {
        action,
        alert: currentAlert.id,
        context: {
          latitude,
          longitude,
          notes: "Suspicious activity detected",
        },
      };
      const result = await dispatchAction(payload);
      toast.success(`Dispatched: ${action}`);

      if (action === "Dispatch Drone" && result?.edge_url) {
        setDispatchEdgeUrl(result.edge_url);
        setDroneStreamEnded(false);
        setVideoRetryCount(0);
      }

      loadAlert();
    } catch (err) {
      console.error("Dispatch error:", err);
      toast.error("Failed to dispatch action");
    }
  };

  const handleDroneStreamEnd = useCallback(() => {
    setDroneStreamEnded(true);
    setDispatchEdgeUrl(null);
    loadAlert();
    setVideoRetryCount(5);
  }, []);

  const handleDroneControl = (control, deviceId) => {
    const actionMap = {
      top: "forward",
      left: "yaw_left",
      right: "yaw_right",
    };
    const action = actionMap[control];
    if (!action) return;

    const payload = {
      type: "device.drone.action",
      message: {
        action,
        device_id: deviceId,
      },
    };
    const sent = sendMessage(payload);
    if (!sent) {
      console.warn("WebSocket not connected, cannot send drone control command");
    }
  };

  const handlePtzAngleChange = (value) => {
    const parsed = Number(value);
    const clamped = Math.min(90, Math.max(0, Number.isNaN(parsed) ? 0 : parsed));
    setPtzAngle(clamped);
  };

  const handleGimbalSet = (deviceId) => {
    const payload = {
      type: "device.drone.gimble",
      message: {
        angle: String(ptzAngle),
        device_id: deviceId,
      },
    };
    const sent = sendMessage(payload);
    if (sent) {
      toast.success(`Gimbal angle set to ${ptzAngle}°`);
    } else {
      toast.error("WebSocket not connected");
    }
  };

  const handleSeek = (e) => {
    if (!playerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;
    playerRef.current.seekTo(percentage);
    setProgressPercent(percentage * 100);
  };

  const handleError = (index) => {
    setDeviceStates((prev) =>
      prev.map((state, i) => (i === index ? { ...state, hasError: true } : state))
    );
  };

  // Main and other feeds swapping
  const mainDevice = displayedDevices[selectedDeviceIndex];
  const otherDevices = displayedDevices.filter((_, idx) => idx !== selectedDeviceIndex);

  // Devices with a genuine failure, excluding the transient "No recording
  // data" drone failure that can appear before the upload completes.
  const failedDevices = displayedDevices.filter(
    (d) =>
      d.alert_device_status?.toLowerCase() === "failed" || d.failure_reason
  ).filter(
    (d) =>
      !(
        isDroneNoRecordingWaiting &&
        d.device_type?.toLowerCase() === "drone" &&
        d.failure_reason?.toLowerCase().includes("no recording data")
      )
  );

  // Status values
  const isAlertComplete = currentAlert?.alert_analysis?.toLowerCase() === "complete" || currentAlert?.alert_analysis?.toLowerCase() === "completed";
  const isAlertFailed = currentAlert?.alert_analysis?.toLowerCase() === "failed" || currentAlert?.status?.toLowerCase() === "failed";
  const isDetailLoading = !localAlert || !localAlert.devices;

  // Seeker info and metadata
  const streamUrl = useMemo(() => {
    if (!mainDevice) return "";
    const cameraId = mainDevice.camera?.id || mainDevice.camera_id || mainDevice.id;
    const isStream = mainDevice.video_url === "stream_url" || !mainDevice.video_url;
    if (isStream) return ENDPOINTS.CAMERA_STREAM(cameraId);
    
    const raw = mainDevice.video_url;
    if (!raw) return "";
    
    // If URL is already complete (starts with http:// or https://), return as-is
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    
    // For relative paths, construct the complete URL
    if (API_BASE_URL) {
      // Ensure no double slashes
      const base = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const path = raw.startsWith("/") ? raw : `/${raw}`;
      const finalUrl = `${base}${path}`;
      console.log(`Constructed video URL: ${finalUrl} (from API_BASE_URL: ${API_BASE_URL}, raw: ${raw})`);
      return finalUrl;
    }
    
    // Fallback: use window origin if API_BASE_URL is not available
    const windowBase = window.location.origin;
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    const finalUrl = `${windowBase}${path}`;
    console.log(`Constructed video URL (fallback): ${finalUrl} (from window.location.origin, raw: ${raw})`);
    return finalUrl;
  }, [mainDevice]);

  const isStreamSentinel = useMemo(() => {
    if (!mainDevice) return false;
    return mainDevice.video_url === "stream_url" || !mainDevice.video_url;
  }, [mainDevice]);

  const isMainDeviceStreaming = useMemo(() => {
    if (!mainDevice) return false;
    return mainDevice.status?.toLowerCase() === "streaming" || mainDevice.status?.toLowerCase() === "active";
  }, [mainDevice]);



  // Dynamic dispatch button calculations
  const recommendedActions = useMemo(() => {
    let llmData = currentAlert?.llm_response;
    if (!llmData || !llmData.recommended_action) return [];
    return Array.isArray(llmData.recommended_action)
      ? llmData.recommended_action
      : [llmData.recommended_action];
  }, [currentAlert]);

  const dispatchMapping = useMemo(() => ({
    "Dispatch Drone": "Drone Dispatched",
    "Dispatch Small Team": "Small Team Dispatched",
    "Dispatch Police": "Police Dispatched",
    "Dispatch Ambulance Service": "Ambulance Dispatched",
    "Dispatch Fire Service": "Fire Service Dispatched",
    "Dispatch Maintenance Team": "Maintenance Team Dispatched",
  }), []);

  // Set of dispatched labels found in the current recommendedActions list
  // e.g. if "Drone Dispatched" is in the list, drone was already dispatched
  const ALL_DISPATCHED_VALUES = useMemo(
    () => new Set(Object.values(dispatchMapping)),
    [dispatchMapping]
  );

  const dispatchedStates = useMemo(() => {
    return new Set(
      recommendedActions.filter((action) => ALL_DISPATCHED_VALUES.has(action))
    );
  }, [recommendedActions, ALL_DISPATCHED_VALUES]);

  const isDroneDispatched = useMemo(() => {
    return (
      recommendedActions.includes("Drone Dispatched") ||
      currentAlert?.alert_analysis === "Drone Dispatched" ||
      currentAlert?.alert_analysis?.toLowerCase() === "complete" ||
      currentAlert?.alert_analysis?.toLowerCase() === "completed"
    );
  }, [recommendedActions, currentAlert?.alert_analysis]);

  // Observed Activities pagination
  const observedActivities = useMemo(() => {
    return currentAlert?.llm_response?.observed_activity || [];
  }, [currentAlert]);

  const totalActivityPages = Math.max(1, Math.ceil(observedActivities.length / activitiesPerPage));
  
  const pagedActivities = useMemo(() => {
    const start = (activityPage - 1) * activitiesPerPage;
    return observedActivities.slice(start, start + activitiesPerPage);
  }, [observedActivities, activityPage]);

  if (!currentAlert) {
    return (
      <div className="alert-detail-wrapper empty-state">
        <h3>{t("label.alertDetails")}</h3>
        <p>{t("label.noAlertSelected")}</p>
        <button onClick={handleBack} className="back-to-list-btn">
          {t("button.backToAlerts")}
        </button>
      </div>
    );
  }

  return (
    <div className={`alert-detail-wrapper ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />

      <main className="alert-detail-main-content">
        {/* Standardized Top Header Bar */}
        <Header title={t("label.alertDetails")} />



        {/* Info Row below Top Bar */}
        <div className="alert-detail-info-row">
          <div className="info-row-left">
            <button
              onClick={handleBack}
              className="alert-detail-back-btn"
              aria-label={t("ariaLabel.goBack")}
            >
              <TablerArrowLeft size={16} />
            </button>
            <div className="info-row-titles">
              <h2 className="info-row-title">
                {currentAlert.message || "Person detected in monitored area"}
              </h2>
              <div className="info-row-subtitle">
                {formatLocalDateTime(currentAlert.start_time)} -{" "}
                {mainDevice?.camera?.name || mainDevice?.camera_name || "Primary Camera"}
              </div>
            </div>
          </div>

          <div className="info-row-right">
            <span className={`detail-severity-badge severity-${currentAlert.severity?.toLowerCase()}`}>
              {currentAlert.severity?.toLowerCase() === "high" && <TablerHigh size={14} />}
              {currentAlert.severity?.toLowerCase() === "medium" && <TablerMedium size={14} />}
              {currentAlert.severity?.toLowerCase() === "low" && <TablerLow size={14} />}
              {formatBadgeLabel(currentAlert.severity || "Low")}
            </span>
            <span className={`detail-status-badge status-${currentAlert.alert_analysis?.toLowerCase()}`}>
              {currentAlert.alert_analysis || "Awaiting review"}
            </span>
          </div>
        </div>

        {/* Outer Split Columns */}
        {/* Outer Split Columns */}
        <div className="alert-detail-body">
          {isDroneNoRecordingWaiting && (
            <div className="alert-failure-reasons-container" style={{
              backgroundColor: "rgba(56, 139, 253, 0.1)",
              border: "1px solid #388bfd",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "20px",
              color: "#fff"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "20px" }}>⏳</span>
                <h3 style={{ margin: 0, color: "#388bfd", fontSize: "16px", fontWeight: "bold" }}>
                  Processing Drone Recording
                </h3>
              </div>
              <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#8b949e" }}>
                The drone has finished streaming but its recording is still being uploaded. Waiting for the video to become available…
              </p>
              <p style={{ margin: 0, fontSize: "12.5px", color: "#c9d1d9" }}>
                Retrying automatically ({videoRetryCount} {videoRetryCount === 1 ? "attempt" : "attempts"} remaining).
              </p>
            </div>
          )}
          {failedDevices.length > 0 && (
            <div className="alert-failure-reasons-container" style={{
              backgroundColor: "rgba(218, 54, 51, 0.1)",
              border: "1px solid #da3633",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "20px",
              color: "#fff"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "20px" }}>⚠️</span>
                <h3 style={{ margin: 0, color: "#da3633", fontSize: "16px", fontWeight: "bold" }}>
                  Alert Process Failure Details
                </h3>
              </div>
              <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#8b949e" }}>
                The following devices encountered issues while recording or processing this alert:
              </p>
              <div style={{ display: "grid", gap: "10px" }}>
                {failedDevices.map(d => (
                  <div key={d.id} style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "10px",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "6px",
                    borderLeft: "4px solid #da3633"
                  }}>
                    <span style={{ fontWeight: "600", fontSize: "13.5px", color: "#f0f6fc" }}>
                      {currentAlert.type !== "sensor"
                        ? d.camera_name || d.device_name || "Camera"
                        : d.device_name || "Sensor"} ({d.device_type || "CAMERA"})
                    </span>
                    <span style={{ fontSize: "12.5px", color: "#c9d1d9", marginTop: "4px" }}>
                      {d.failure_reason || "Unknown failure"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="video-and-sidebar-row">
            {/* Left Main Stream Component */}
            <div className="detail-video-stream-container">
              <div className="stream-frame-box" ref={videoContainerRef}>
                {isDetailLoading ? (
                  <div className="detail-stream-placeholder">
                    <div className="stream-loading-spinner"></div>
                    <div className="placeholder-text">Loading stream...</div>
                  </div>
                ) : mainDevice?.device_type?.toLowerCase() === "drone" && currentAlert?.drone?.edge_url && isStreamSentinel && !droneStreamEnded ? (
                  <WhepVideoPlayer edgeUrl={currentAlert.drone.edge_url} onStreamEnd={handleDroneStreamEnd} />
                ) : streamUrl && !deviceStates[selectedDeviceIndex]?.hasError && mainDevice?.alert_device_status?.toLowerCase() !== "failed" ? (
                  <>
                    {isStreamSentinel ? (
                      <>
                        <img
                          key={`${selectedDeviceIndex}_${streamUrl}`}
                          src={streamUrl}
                          alt={mainDevice?.camera_name || "Live feed"}
                          className="detail-stream-element detail-stream-img"
                          onError={() => handleError(selectedDeviceIndex)}
                        />
                        <button
                          type="button"
                          className="live-maximize-btn"
                          onClick={(e) => { e.stopPropagation(); handleMaximize(); }}
                          title={isFullscreen ? t("label.exitFullscreen") : t("label.fullscreen")}
                        >
                          {isFullscreen ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                            </svg>
                          )}
                        </button>
                      </>
                    ) : (
                      <video
                        key={`${selectedDeviceIndex}_${streamUrl}`}
                        ref={playerRef}
                        src={streamUrl}
                        className="detail-stream-element"
                        style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000', cursor: 'pointer' }}
                        onClick={() => {
                          if (playerRef.current) {
                            if (playerRef.current.paused) {
                              playerRef.current.play().catch(err => console.error("Play failed:", err));
                            } else {
                              playerRef.current.pause();
                            }
                          }
                        }}
                        autoPlay
                        muted
                        playsInline
                        crossOrigin="anonymous"
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onTimeUpdate={(e) => {
                          if (e.target.duration) {
                            setProgressPercent((e.target.currentTime / e.target.duration) * 100);
                            const mins = Math.floor(e.target.currentTime / 60);
                            const secs = Math.floor(e.target.currentTime % 60);
                            setElapsedTime(`${mins}:${String(secs).padStart(2, '0')}`);
                          }
                        }}
                        onLoadedMetadata={(e) => {
                          const mins = Math.floor(e.target.duration / 60);
                          const secs = Math.floor(e.target.duration % 60);
                          setTotalDuration(`${mins}:${String(secs).padStart(2, '0')}`);
                        }}
                        onError={(e) => {
                          console.error(`HTML5 video error for URL ${streamUrl}:`, e.target.error);
                          setIsPlaying(false);
                          handleError(selectedDeviceIndex);
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}

                    {/* Media Seek Bar Play Indicator */}
                    {isStreamSentinel && (
                      <div className="video-overlay-live">
                        <span className="live-dot-pulsing"></span>LIVE
                      </div>
                    )}
                    {!isStreamSentinel && !isPlaying && (
                      <div className="video-overlay-play-btn" onClick={() => playerRef.current && playerRef.current.play().catch(err => console.error(err))}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: "3px" }}>
                          <polygon points="6 3 20 12 6 21 6 3"></polygon>
                        </svg>
                      </div>
                    )}
                    {!isStreamSentinel && (
                      <div 
                        className="video-overlay-controls"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (playerRef.current) {
                            if (playerRef.current.paused) {
                              playerRef.current.play().catch(err => console.error("Play failed:", err));
                            } else {
                              playerRef.current.pause();
                            }
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <button
                          className="video-control-play-pause-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (playerRef.current) {
                              if (playerRef.current.paused) {
                                playerRef.current.play().catch(err => console.error("Play failed:", err));
                              } else {
                                playerRef.current.pause();
                              }
                            }
                          }}
                          title={isPlaying ? t("label.pause") : t("label.play")}
                        >
                          {isPlaying ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="6" y="3" width="4" height="18" />
                              <rect x="14" y="3" width="4" height="18" />
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: "1px" }}>
                              <polygon points="6 3 20 12 6 21 6 3"></polygon>
                            </svg>
                          )}
                        </button>
                        <span className="time-elapsed">{elapsedTime}</span>
                        <div className="seekbar-container" onClick={(e) => { e.stopPropagation(); handleSeek(e); }}>
                          <div className="seekbar-progress" style={{ width: `${progressPercent}%` }}></div>
                        </div>
                        <span className="time-total">{totalDuration}</span>
                        <button
                          type="button"
                          className="video-control-maximize-btn"
                          onClick={(e) => { e.stopPropagation(); handleMaximize(); }}
                          title={isFullscreen ? t("label.exitFullscreen") : t("label.fullscreen")}
                        >
                          {isFullscreen ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                            </svg>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Live drone fly controls overlay */}
                    {mainDevice?.device_type?.toLowerCase() === "drone" &&
                      mainDevice?.alert_device_status?.toLowerCase() === "recording" && (
                        <div className="drone-controls-overlay">
                          <button
                            type="button"
                            className="drone-control-btn drone-control-top"
                            onClick={() => handleDroneControl("top", mainDevice.camera?.id || mainDevice.id)}
                            aria-label={t("label.moveForward")}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="drone-control-btn drone-control-left"
                            onClick={() => handleDroneControl("left", mainDevice.camera?.id || mainDevice.id)}
                            aria-label={t("label.yawLeft")}
                          >
                            ◀
                          </button>
                          <button
                            type="button"
                            className="drone-control-btn drone-control-right"
                            onClick={() => handleDroneControl("right", mainDevice.camera?.id || mainDevice.id)}
                            aria-label={t("label.yawRight")}
                          >
                            ▶
                          </button>
                        </div>
                      )}
                  </>
                ) : (
                  <div className="detail-stream-placeholder error-state">
                    <svg className="placeholder-icon" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34" />
                      <path d="M23 7l-7 5 7 5V7z" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                    <div className="placeholder-text">
                      Video stream not available for{" "}
                      {currentAlert.type === "sensor"
                        ? mainDevice?.device_name || "Sensor"
                        : mainDevice?.camera_name || "Camera"}
                    </div>
                    {mainDevice?.failure_reason && (
                      <div className="placeholder-failure-reason" style={{ marginTop: "12px", color: "#ef4444", fontSize: "14px", fontWeight: "bold" }}>
                        Failure Reason: {mainDevice.failure_reason}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Feed Name Row */}
              <div className="detail-feed-info-bar">
                <div className="feed-info-left">
                  <span className="info-camera-name">
                    {currentAlert.type !== "sensor"
                      ? mainDevice?.camera?.name || mainDevice?.camera_name || "Primary Camera"
                      : mainDevice?.device_name || "Sensor Device"}
                  </span>
                </div>
                <div className="feed-info-right">
                  <span className="info-device-type">
                    {mainDevice?.device_type?.toUpperCase() || "CAMERA"}
                  </span>
                  <span className={`info-stream-status-badge ${mainDevice?.alert_device_status?.toLowerCase() === "failed" ? "failed" : isMainDeviceStreaming ? "streaming" : "inactive"}`}>
                    {mainDevice?.alert_device_status?.toLowerCase() === "failed" ? "FAILED" : isMainDeviceStreaming ? "STREAMING" : "INACTIVE"}
                  </span>
                </div>
                
                {mainDevice?.device_type?.toLowerCase() === "drone" && (
                  <div className="feed-info-right-controls">
                    {/* Drone RTL & LAND Buttons */}
                    <button
                      className="inline-btn rtl-btn"
                      disabled={disabledRTL}
                      onClick={() => sendDroneCommand("RTL")}
                      title={t("label.returnToLaunch")}
                    >
                      {t("button.rtl")}
                    </button>
                    <button
                      className="inline-btn land-btn"
                      disabled={disabledLAND}
                      onClick={() => sendDroneCommand("LAND")}
                      title={t("label.emergencyLand")}
                    >
                      {t("button.land")}
                    </button>

                    {/* Gimbal Angle Control */}
                    <div className="gimbal-control-inline">
                      <label htmlFor="ptzAngleInput">{t("label.gimbalAngle")}</label>
                      <input
                        id="ptzAngleInput"
                        type="number"
                        min="0"
                        max="90"
                        value={ptzAngle}
                        onChange={(e) => handlePtzAngleChange(e.target.value)}
                        className="ptz-angle-input-field"
                      />
                      <button
                        className="ptz-set-btn-inline"
                        onClick={() => handleGimbalSet(mainDevice.camera?.id || mainDevice.id)}
                      >
                        {t("button.set")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column other camera/drone feeds */}
            <div className="alert-detail-other-feeds-sidebar">
              <div className="other-feeds-header">
                <h2 className="other-feeds-sidebar-title">
                  {t("label.otherFeeds")}
                </h2>
                <span className="other-feeds-count">{otherDevices.length}</span>
              </div>

              <div className="other-feeds-list">
                {otherDevices.length === 0 ? (
                  <div className="other-feeds-empty">{t("label.noAlternativeFeeds")}</div>
                ) : (
                  otherDevices.map((device) => {
                    const idxInAll = displayedDevices.findIndex((d) => d.id === device.id);
                    const isStreaming = device?.status?.toLowerCase() === "streaming" || device?.status?.toLowerCase() === "active";
                    const isFailed = device?.alert_device_status?.toLowerCase() === "failed";

                    return (
                      <div
                        key={device.id}
                        className="other-feed-side-card"
                        onClick={() => setSelectedDeviceId(device.id)}
                      >
                        <div className="side-card-video-box">
                          <svg className="side-card-play-icon" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                          </svg>
                        </div>

                        <div className="side-card-content">
                          <div className="side-card-name">
                            {currentAlert.type !== "sensor"
                              ? device?.camera?.name || device?.camera_name || "Camera Feed"
                              : device?.device_name || "Sensor"}
                          </div>
                          <div className="side-card-meta">
                            <span className="side-card-type">
                              {device?.device_type || "CAMERA"}
                            </span>
                            <span className={`side-card-status-badge ${isFailed ? "failed" : isStreaming ? "streaming" : "inactive"}`}>
                              {isFailed ? "FAILED" : isStreaming ? "STREAMING" : "INACTIVE"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {!isAlertComplete && (
            <div className="response-actions-card">
              <h2 className="response-actions-title">{t("label.responseAction")}</h2>
              <div className="response-buttons-row">
                {/* Dispatch actions — hidden for failed alerts */}
                {!isAlertFailed && (
                <>
                {/* Dispatch Drone */}
                {(recommendedActions.includes("Dispatch Drone") || isDroneDispatched) && (
                  <button
                    className={`resp-action-btn drone-btn ${isDroneDispatched ? "dispatched" : "primary"}`}
                    onClick={() => handleDispatch("Dispatch Drone")}
                    disabled={isDroneDispatched}
                  >
                    {isDroneDispatched ? (
                      t("button.dispatchedDrone")
                    ) : (
                      <>
                        <TablerDrone size={16} />
                        {t("button.dispatchDrone")}
                      </>
                    )}
                  </button>
                )}

                {/* Dispatch Small Team */}
                {(recommendedActions.includes("Dispatch Small Team") || dispatchedStates.has("Small Team Dispatched")) && (
                  <button
                    className="resp-action-btn outline"
                    onClick={() => handleDispatch("Dispatch Small Team")}
                    disabled={isAlertComplete || dispatchedStates.has("Small Team Dispatched")}
                  >
                    {dispatchedStates.has("Small Team Dispatched") ? (
                      t("button.dispatchedSmallTeam")
                    ) : (
                      <>
                        <GroupIcon width={16} height={16} stroke="currentColor" />
                        {t("button.dispatchSmallTeam")}
                      </>
                    )}
                  </button>
                )}

                {/* Dispatch Police */}
                {(recommendedActions.includes("Dispatch Police") || dispatchedStates.has("Police Dispatched")) && (
                  <button
                    className="resp-action-btn outline"
                    onClick={() => handleDispatch("Dispatch Police")}
                    disabled={isAlertComplete || dispatchedStates.has("Police Dispatched")}
                  >
                    {dispatchedStates.has("Police Dispatched") ? (
                      t("button.dispatchedPolice")
                    ) : (
                      <>
                        <ShieldIcon size={16} />
                        {t("button.dispatchPolice")}
                      </>
                    )}
                  </button>
                )}

                {/* Dispatch Ambulance Service */}
                {(recommendedActions.includes("Dispatch Ambulance Service") || dispatchedStates.has("Ambulance Dispatched")) && (
                  <button
                    className="resp-action-btn outline"
                    onClick={() => handleDispatch("Dispatch Ambulance Service")}
                    disabled={isAlertComplete || dispatchedStates.has("Ambulance Dispatched")}
                  >
                    {dispatchedStates.has("Ambulance Dispatched") ? (
                      t("button.dispatchedAmbulance")
                    ) : (
                      <>
                        <TablerAlertTriangle size={16} />
                        {t("button.dispatchAmbulance")}
                      </>
                    )}
                  </button>
                )}

                {/* Dispatch Fire Service */}
                {(recommendedActions.includes("Dispatch Fire Service") || dispatchedStates.has("Fire Service Dispatched")) && (
                  <button
                    className="resp-action-btn outline"
                    onClick={() => handleDispatch("Dispatch Fire Service")}
                    disabled={isAlertComplete || dispatchedStates.has("Fire Service Dispatched")}
                  >
                    {dispatchedStates.has("Fire Service Dispatched") ? (
                      t("button.dispatchedFireService")
                    ) : (
                      <>
                        {t("button.dispatchFireService")}
                      </>
                    )}
                  </button>
                )}

                {/* Dispatch Maintenance Team */}
                {(recommendedActions.includes("Dispatch Maintenance Team") || dispatchedStates.has("Maintenance Team Dispatched")) && (
                  <button
                    className="resp-action-btn outline"
                    onClick={() => handleDispatch("Dispatch Maintenance Team")}
                    disabled={isAlertComplete || dispatchedStates.has("Maintenance Team Dispatched")}
                  >
                    {dispatchedStates.has("Maintenance Team Dispatched") ? (
                      t("button.dispatchedMaintenance")
                    ) : (
                      <>
                        {t("button.dispatchMaintenance")}
                      </>
                    )}
                  </button>
                )}
                </>)}
                
                {!isAlertFailed && !isAlertComplete && (
                  <button
                    className="resp-action-btn outline close-btn"
                    onClick={handleCloseToggle}
                  >
                    <TablerX size={16} />
                    {t("button.closeAlert")}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Close Reason Box */}
          <div className={`close-input-box-container ${showCloseInput ? "open" : ""}`}>
            <div className="close-input-box-inner">
              <label htmlFor="closeReason" className="close-box-label">
                {t("label.reasonForClosingAlert")}
              </label>
              <textarea
                id="closeReason"
                className="close-box-textarea"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder={t("placeholder.enterCloseReason")}
                rows={3}
              />
              <div className="close-box-buttons">
                <button
                  onClick={handleCloseSubmit}
                  disabled={isSubmittingClose}
                  className="close-box-btn-submit"
                >
                  {isSubmittingClose ? t("button.submitting") : t("button.submit")}
                </button>
                <button
                  onClick={handleCloseCancel}
                  disabled={isSubmittingClose}
                  className="close-box-btn-cancel"
                >
                  {t("button.cancel")}
                </button>
              </div>
            </div>
          </div>

          {/* Combined Alert Details Container Card */}
          <div className="alert-details-container-card">
            {/* Section details header */}
            <div className="alert-details-header-section">
              <h2 className="alert-details-section-title">{t("label.alertDetails")}</h2>
              <div className="alert-details-section-subtitle">
                {formatLocalDateTime(currentAlert.start_time)}
              </div>
            </div>

            {/* Description highlight card */}
            <div className="alert-description-card">
              <p className="description-text">
                {currentAlert.message || t("label.alertDescriptionProcessing")}
              </p>
            </div>

            {/* Closed Alert details */}
            {(currentAlert?.is_resolved || currentAlert?.alert_analysis?.toLowerCase() === "complete" || currentAlert?.alert_analysis?.toLowerCase() === "completed") && (
              <div className="alert-closure-card">
                <h3 className="closure-title">
                  <span className="closure-dot-active"></span>
                  {t("label.closureDetails")}
                </h3>
                <div className="closure-grid">
                  <div className="closure-item">
                    <span className="closure-label">{t("label.closedTime")}</span>
                    <span className="closure-value">
                      {currentAlert.resolved_at ? formatLocalDateTime(currentAlert.resolved_at) : "N/A"}
                    </span>
                  </div>
                  <div className="closure-item">
                    <span className="closure-label">{t("label.closedBy")}</span>
                    <span className="closure-value">
                      {currentAlert.resolved_by || "Operator"}
                    </span>
                  </div>
                  <div className="closure-item full-width">
                    <span className="closure-label">{t("label.reasonForClosing")}</span>
                    <p className="closure-reason-text">
                      {currentAlert.resolution_notes || "No reason provided."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* START / END / LLM RESPONSE / RISK ASSESSMENT */}
          <div className="status-grid-row">
            <div className="grid-status-box">
              <span className="box-title">{t("label.start")}</span>
              <span className="box-value">
                {currentAlert.type === "sensor"
                  ? formatLocalTime(displayedDevices[0]?.start_time || currentAlert.start_time)
                  : formatLocalTime(currentAlert.start_time)}
              </span>
            </div>
            <div className="grid-status-box">
              <span className="box-title">{t("label.end")}</span>
              <span className="box-value">
                {currentAlert.type === "sensor"
                  ? (displayedDevices[0]?.end_time || currentAlert.end_time ? formatLocalTime(displayedDevices[0]?.end_time || currentAlert.end_time) : "")
                  : (currentAlert.end_time ? formatLocalTime(currentAlert.end_time) : "")}
              </span>
            </div>
            <div className="grid-status-box">
              <span className="box-title">{t("label.llmResponse")}</span>
              <span className="box-value">
                {currentAlert.llm_response?.incident_category || t("label.processing")}
              </span>
            </div>
            <div className="grid-status-box risk-assessment">
              <span className="box-title">{t("label.riskAssessment")}</span>
              <span className={`box-value risk-${currentAlert.llm_response?.risk_assessment?.toLowerCase()}`}>
                {currentAlert.llm_response?.risk_assessment || t("label.nameN/A")}
              </span>
            </div>
          </div>

          {/* Observed Activity & Next Actions columns side-by-side */}
          <div className="activity-breakdown-row">
            {/* Observed Activity */}
            <div className="breakdown-column">
              <h3 className="column-title">{t("label.observedActivity")}</h3>
              <ul className="breakdown-list">
                {observedActivities.length > 0 ? (
                  pagedActivities.map((activity, idx) => (
                    <li key={idx} className="breakdown-item">
                      <span className="bullet-dot">•</span>
                      {activity}
                    </li>
                  ))
                ) : (
                  <li className="breakdown-item no-data">{t("label.noObservedActivities")}</li>
                )}
              </ul>

              {/* Paginated Navigation Controls */}
              {totalActivityPages > 1 && (
                <div className="activity-paging-controls">
                  <button
                    className="paging-arrow-btn"
                    disabled={activityPage === 1}
                    onClick={() => setActivityPage((prev) => Math.max(1, prev - 1))}
                    aria-label={t("ariaLabel.previousPage")}
                  >
                    {"<"}
                  </button>
                  <span className="paging-info-label">
                    {activityPage} of {totalActivityPages}
                  </span>
                  <button
                    className="paging-arrow-btn"
                    disabled={activityPage === totalActivityPages}
                    onClick={() => setActivityPage((prev) => Math.min(totalActivityPages, prev + 1))}
                    aria-label={t("ariaLabel.nextPage")}
                  >
                    {">"}
                  </button>
                </div>
              )}
            </div>

            {/* Next Actions */}
            <div className="breakdown-column">
              <h3 className="column-title">{t("label.nextActions")}</h3>
              <ul className="breakdown-list">
                {currentAlert.llm_response?.next_action?.length > 0 ? (
                  currentAlert.llm_response.next_action.map((action, idx) => (
                    <li key={idx} className="breakdown-item">
                      <span className="bullet-dot">•</span>
                      {action}
                    </li>
                  ))
                ) : (
                  <li className="breakdown-item no-data">{t("label.noRecommendedActions")}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Warning modal: cannot close */}
      {showCannotCloseModal && (
        <div className="warning-modal-overlay" role="dialog" aria-modal="true">
          <div className="warning-modal">
            <div className="warning-modal-header">
              <span className="warning-icon">⚠️</span>
              <h3>{t("label.cannotCloseTitle")}</h3>
            </div>
            <div className="warning-modal-body">
              {t("label.cannotCloseBody")}
            </div>
            <div className="warning-modal-footer">
              <button className="warning-modal-ok" onClick={() => setShowCannotCloseModal(false)}>
                {t("button.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mission Dialogue Popup */}
      {showMissionDialog && missionData && (
        <div className="mission-dialog-overlay">
          <div className="mission-dialog">
            <h3 className="mission-dialog-title">{t("label.missionApprovalRequired")}</h3>
            <div className="mission-dialog-content">
              <div className="mission-dialog-device">
                <span className="mission-dialog-device-label">{t("label.device")}</span>
                <span className="mission-dialog-device-value">{missionData.device_name}</span>
              </div>
              <div className="mission-dialog-inject-row">
                <span className="mission-dialog-inject-label">{t("label.missionInfo")}</span>
                <p className="mission-dialog-inject-value">{missionData.inject_state}</p>
              </div>
            </div>
            <p className="mission-dialog-question">{t("label.approveMission")}</p>
            <div className="mission-dialog-buttons">
              <button
                onClick={async () => {
                  const droneId = missionData?.drone_id;
                  if (droneId) {
                    try {
                      const res = await fetch(ENDPOINTS.DRONE_INJECT_APPROVAL(droneId), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ approved: true }),
                      });
                      if (res.ok) {
                        toast.success("Mission approved successfully");
                      } else {
                        toast.error("Failed to approve mission");
                      }
                    } catch (err) {
                      console.error("Error approving mission", err);
                      toast.error("Error approving mission");
                    }
                  }
                  setShowMissionDialog(false);
                  setMissionData(null);
                }}
                className="mission-dialog-btn-yes"
              >
                {t("button.approve")}
              </button>
              <button
                onClick={() => {
                  setShowMissionDialog(false);
                  setMissionData(null);
                }}
                className="mission-dialog-btn-no"
              >
                {t("button.reject")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlertDetailPage;
